import React, { useState } from 'react';
import {
	Alert,
	Button,
	FormControlLabel,
	Grid,
	InputAdornment,
	Stack,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { updatePassengerBillingSettings } from '../../services';
import { IPassengerDetail } from '../../types';

/**
 * Ficha de facturación del cliente (persona jurídica): día límite de recepción
 * de facturas, tope de crédito y notas. El PUT es un reemplazo total, por eso
 * siempre se envían los tres campos (vacío = null en la DB).
 */

interface PassengerBillingSettingsProps {
	passengerDetail: IPassengerDetail;
}

const MIN_DEADLINE_DAY = 1;
const MAX_DEADLINE_DAY = 30;

export const PassengerBillingSettings = ({ passengerDetail }: PassengerBillingSettingsProps): JSX.Element => {
	const [deadlineDay, setDeadlineDay] = useState<string>(
		passengerDetail.invoiceReceptionDeadlineDay != null ? String(passengerDetail.invoiceReceptionDeadlineDay) : '',
	);
	const [maximumCreditAmount, setMaximumCreditAmount] = useState<string>(
		passengerDetail.maximumCreditAmount != null ? String(passengerDetail.maximumCreditAmount) : '',
	);
	const [notes, setNotes] = useState<string>(passengerDetail.notes ?? '');
	const [requiresPurchaseOrder, setRequiresPurchaseOrder] = useState<boolean>(
		passengerDetail.requiresPurchaseOrder ?? false,
	);

	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);

	const parsedDeadlineDay = deadlineDay.trim() === '' ? undefined : Number(deadlineDay);
	const parsedMaxCredit = maximumCreditAmount.trim() === '' ? undefined : Number(maximumCreditAmount);

	const deadlineDayInvalid =
		parsedDeadlineDay !== undefined &&
		(!Number.isInteger(parsedDeadlineDay) ||
			parsedDeadlineDay < MIN_DEADLINE_DAY ||
			parsedDeadlineDay > MAX_DEADLINE_DAY);
	const maxCreditInvalid = parsedMaxCredit !== undefined && (Number.isNaN(parsedMaxCredit) || parsedMaxCredit < 0);

	const handleSave = async (): Promise<void> => {
		if (deadlineDayInvalid || maxCreditInvalid) return;

		setSaving(true);
		setError(null);
		setSaved(false);

		try {
			await updatePassengerBillingSettings(passengerDetail.keyId, {
				invoiceReceptionDeadlineDay: parsedDeadlineDay,
				maximumCreditAmount: parsedMaxCredit,
				requiresPurchaseOrder: requiresPurchaseOrder || undefined,
				notes: notes.trim() || undefined,
			});
			setSaved(true);
		} catch {
			setError('No se pudo guardar la configuración de facturación.');
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						size="small"
						type="number"
						label="Día máx. recepción de facturas"
						value={deadlineDay}
						onChange={(e) => {
							setSaved(false);
							setDeadlineDay(e.target.value);
						}}
						error={deadlineDayInvalid}
						helperText={
							deadlineDayInvalid
								? `Debe ser un día entre ${MIN_DEADLINE_DAY} y ${MAX_DEADLINE_DAY}`
								: 'Día del mes hasta el cual el cliente recibe facturas'
						}
						inputProps={{ min: MIN_DEADLINE_DAY, max: MAX_DEADLINE_DAY, step: 1 }}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						size="small"
						type="number"
						label="Monto máximo de crédito"
						value={maximumCreditAmount}
						onChange={(e) => {
							setSaved(false);
							setMaximumCreditAmount(e.target.value);
						}}
						error={maxCreditInvalid}
						helperText={maxCreditInvalid ? 'No puede ser negativo' : 'Tope para la suma de saldos de facturas'}
						InputProps={{ startAdornment: <InputAdornment position="start">USD</InputAdornment> }}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<Tooltip title="Al facturar sin referencia de cliente (OC) en la orden, se muestra un aviso">
						<FormControlLabel
							control={
								<Switch
									checked={requiresPurchaseOrder}
									onChange={(e) => {
										setSaved(false);
										setRequiresPurchaseOrder(e.target.checked);
									}}
									color="warning"
								/>
							}
							label="Necesita orden de compra (OC) para facturar"
						/>
					</Tooltip>
				</Grid>

				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						size="small"
						label="Notas del cliente"
						placeholder="Detalles relevantes para facturarle a este cliente (admite markdown)"
						value={notes}
						onChange={(e) => {
							setSaved(false);
							setNotes(e.target.value);
						}}
						multiline
						minRows={3}
					/>
				</Grid>
			</Grid>

			{error && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}

			<Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
				<Button
					variant="contained"
					size="small"
					startIcon={<SaveIcon />}
					onClick={handleSave}
					disabled={saving || deadlineDayInvalid || maxCreditInvalid}
				>
					{saving ? 'Guardando…' : 'Guardar'}
				</Button>
				{saved && (
					<Typography variant="caption" color="success.main">
						✓ Configuración guardada
					</Typography>
				)}
			</Stack>
		</>
	);
};

export default PassengerBillingSettings;
